// Tabs/ChildrenTab.tsx
import { useState } from 'react';
import {
  Avatar, Text, ActionIcon, Group, Modal, Stack, Button, TextInput,
  Select, Badge, Center, Alert, Card, SimpleGrid, Tooltip,
} from '@mantine/core';
import {
  IconEdit, IconTrash, IconPlus, IconAlertCircle, IconBabyCarriage, IconCake, IconRefresh,
} from '@tabler/icons-react';
import { DateInput } from '@mantine/dates';
import {
  useRegistrationService,
  RegistrationCourseCustomDataModelView,
} from '@actschurch/ccms-sdk';
import ConfirmActionModal from '../Registration/[RegistrationDetails]/Tabs/ConfirmActionModal';

export interface Child {
  ChildName: string;
  ChildSurname: string;
  ChildDateOfBirth: string;
  ChildGender: string;
}

export interface ChildrenTabProps {
  registrationId: number;
  active: boolean;
  /** The raw custom-data row backing the "children" field, or null if this registration has none. */
  row: RegistrationCourseCustomDataModelView | null;
  /** Called after a successful save so the parent can refetch and re-derive `row`. */
  onChanged: () => void | Promise<void>;
    /** Facilitators can add/edit children but not remove them. Defaults to true (admin/leader). */
  canDelete?: boolean;
}

/**
 * Parses the "children" field's stored value into a Child[].
 *
 * ⚠️ The API stores this as a JSON string inside `valueText`/`value` (fieldTypeCode "text" — there is
 * no dedicated field type for a repeatable child group), so a malformed or hand-edited string is
 * always possible. Returns [] rather than throwing so a bad row degrades to "no children" instead of
 * breaking the tab.
 */
export function parseChildren(row: RegistrationCourseCustomDataModelView | null | undefined): Child[] {
  const raw = row?.valueText ?? row?.value ?? '';
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

const emptyChild = (): Child => ({
  ChildName: '', ChildSurname: '', ChildDateOfBirth: '', ChildGender: '',
});

const fullName = (c: Child) =>
  [c.ChildName, c.ChildSurname].filter(Boolean).join(' ').trim() || 'Unnamed Child';

const getInitials = (c: Child) =>
  [c.ChildName?.[0], c.ChildSurname?.[0]].filter(Boolean).join('').toUpperCase() || 'C';

function parseDateValue(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function formatDateValue(value: Date | null): string {
  if (!value) return '';
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

// ── Child grid card ─────────────────────────────────────────────────────
function ChildCard({
  child, onEdit, onDelete,
}: {
  child: Child;
  onEdit: () => void;
  onDelete?: () => void;
}) {
  return (
    <Card withBorder radius="md" padding="lg" h="100%">
      <Group justify="center" mb="sm">
        <Avatar size="xl" radius="md" color={child.ChildGender === 'Male' ? 'blue' : 'pink'}>
          {getInitials(child)}
        </Avatar>
      </Group>

      <Text fw={700} ta="center" size="md" mb={4} lineClamp={1}>
        {fullName(child)}
      </Text>

      <Group justify="center" gap={6} mb={4}>
        {child.ChildGender && (
          <Badge size="sm" variant="light" color={child.ChildGender === 'Male' ? 'blue' : 'pink'}>
            {child.ChildGender}
          </Badge>
        )}
      </Group>

      <Stack align="center" gap={2} mt="xs">
        {child.ChildDateOfBirth ? (
          <Group gap={6} wrap="nowrap">
            <IconCake size={13} style={{ flexShrink: 0, color: 'var(--mantine-color-dimmed)' }} />
            <Text size="sm" c="dimmed">{child.ChildDateOfBirth}</Text>
          </Group>
        ) : (
          <Text size="sm" c="dimmed">No date of birth on record</Text>
        )}
      </Stack>

      <Group justify="flex-end" gap={6} mt="md">
        <Tooltip label="Edit child" withArrow>
          <ActionIcon variant="light" onClick={onEdit}>
            <IconEdit size={16} />
          </ActionIcon>
        </Tooltip>
           {onDelete && (
        <Tooltip label="Remove child" withArrow>
          <ActionIcon variant="light" color="red" onClick={onDelete}>
            <IconTrash size={16} />
          </ActionIcon>
        </Tooltip>
         )}
      </Group>
    </Card>
  );
}

export function ChildrenTab({ registrationId, active, row, onChanged, canDelete = true }: ChildrenTabProps) {
  const registrationService = useRegistrationService();

  const [editIndex, setEditIndex] = useState<number | null>(null); // null = not editing, -1 = adding new
  const [draft, setDraft] = useState<Child>(emptyChild());
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const children = parseChildren(row);

  const openEdit = (index: number) => {
    setDraft({ ...children[index] });
    setEditIndex(index);
  };

  const openAdd = () => {
    setDraft(emptyChild());
    setEditIndex(-1);
  };

  const closeModal = () => {
    setEditIndex(null);
    setDraft(emptyChild());
    setError(null);
  };

  const persist = async (next: Child[]) => {
    if (!row) return;
    setSaving(true);
    setError(null);
    try {
      await (registrationService as any).updateCourseCustomData(row.registrationCourseCustomDataID, {
        value: JSON.stringify(next),
      });
      closeModal();
      await onChanged();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save children.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!draft.ChildName.trim() || !draft.ChildSurname.trim()) return;
    const next = [...children];
    if (editIndex === -1) {
      next.push(draft);
    } else if (editIndex !== null) {
      next[editIndex] = draft;
    }
    persist(next);
  };

  const handleDelete = (index: number) => {
    setDeleteIndex(index);
  };

  const confirmDelete = async () => {
    if (deleteIndex === null) return;
    const next = children.filter((_, i) => i !== deleteIndex);
    await persist(next);
    setDeleteIndex(null);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await onChanged();
    } finally {
      setRefreshing(false);
    }
  };

  // Parent controls tab visibility, but guard rendering anyway in case of a stale `active` mount.
  if (!row) return null;

  return (
    <Stack gap="lg">
      {error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Group justify="space-between" align="center">
        <Text fw={600} size="sm" c="dimmed">
          {children.length} {children.length === 1 ? 'child' : 'children'} on record
        </Text>
        <Group gap="xs">
          <Tooltip label="Refresh children" withArrow>
            <ActionIcon
              variant="subtle"
              aria-label="Refresh children"
              onClick={handleRefresh}
              loading={refreshing}
              size="lg"
            >
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
          <Button size="sm" leftSection={<IconPlus size={14} />} onClick={openAdd}>
            Add Child
          </Button>
        </Group>
      </Group>

      {children.length === 0 ? (
        <Center py="xl">
          <Stack align="center" gap="sm">
            <IconBabyCarriage size={40} style={{ opacity: 0.5 }} />
            <Text fw={600}>No children yet</Text>
            <Text size="sm" c="dimmed" ta="center" maw={360}>
              No children have been captured for this registration yet.
            </Text>
          </Stack>
        </Center>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
          {children.map((child, index) => (
            <ChildCard
              key={`${child.ChildName}-${child.ChildSurname}-${index}`}
              child={child}
              onEdit={() => openEdit(index)}
              onDelete={canDelete ? () => handleDelete(index) : undefined}
            />
          ))}
        </SimpleGrid>
      )}

      <Modal
        opened={editIndex !== null}
        onClose={closeModal}
        title={editIndex === -1 ? 'Add Child' : 'Edit Child'}
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="First name"
            value={draft.ChildName}
            onChange={(e) => setDraft((d) => ({ ...d, ChildName: e.currentTarget.value }))}
            required
          />
          <TextInput
            label="Surname"
            value={draft.ChildSurname}
            onChange={(e) => setDraft((d) => ({ ...d, ChildSurname: e.currentTarget.value }))}
            required
          />
          <DateInput
            label="Date of birth"
            value={parseDateValue(draft.ChildDateOfBirth)}
            onChange={(value) => setDraft((d) => ({
              ...d,
              ChildDateOfBirth: formatDateValue(value),
            }))}
            clearable
          />
          <Select
            label="Gender"
            data={['Male', 'Female']}
            value={draft.ChildGender || null}
            onChange={(v) => setDraft((d) => ({ ...d, ChildGender: v ?? '' }))}
            clearable
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeModal}>Cancel</Button>
            <Button
              loading={saving}
              disabled={!draft.ChildName.trim() || !draft.ChildSurname.trim()}
              onClick={handleSave}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
      
      {canDelete && (
      <ConfirmActionModal
        opened={deleteIndex !== null}
        onClose={() => setDeleteIndex(null)}
        title="Remove child"
        message={
          deleteIndex !== null
            ? `Remove ${fullName(children[deleteIndex])} from this registration?`
            : 'Remove this child from the registration?'
        }
        color="red"
        onConfirm={confirmDelete}
      />
      )}
    </Stack>
  );
}

export default ChildrenTab;